import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import { apiClient } from '../../services/api';
import { tokenManager } from '../../services/authService';

interface ReservationItem {
  id: string;
  productId: string;
  quantity: number;
  price: number;
  product: {
    name: string;
    sku: string;
  };
}

interface Reservation {
  id: string;
  trackingNumber: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  totalAmount: number;
  totalItems: number;
  status: string;
  expiresAt: string;
  createdAt: string;
  notes?: string;
  items: ReservationItem[];
}

export const MyReservationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReservations = async () => {
      const accessToken = tokenManager.getAccessToken();

      if (!accessToken) {
        navigate('/login');
        return;
      }

      try {
        const response = await apiClient.get('/reservations/my-reservations');
        setReservations(response.data);
      } catch (err: any) {
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to load reservations');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchReservations();
  }, [navigate]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
      case 'expired':
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
        My Reservations
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {reservations.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            No reservations yet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You haven't made any reservations. Browse products and checkout to create your first reservation!
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {reservations.map((reservation) => (
            <Grid item xs={12} key={reservation.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {reservation.trackingNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Created: {new Date(reservation.createdAt).toLocaleDateString()} at{' '}
                        {new Date(reservation.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Box>
                    <Chip
                      label={reservation.status.toUpperCase()}
                      color={getStatusColor(reservation.status) as any}
                      size="small"
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        {typeof reservation.totalAmount === 'string'
                          ? parseFloat(reservation.totalAmount).toFixed(2)
                          : reservation.totalAmount.toFixed(2)}{' '}
                        DZD
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total Items
                      </Typography>
                      <Typography variant="h6">{reservation.totalItems}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Expires At
                      </Typography>
                      <Typography variant="body1">
                        {new Date(reservation.expiresAt).toLocaleDateString()} at{' '}
                        {new Date(reservation.expiresAt).toLocaleTimeString()}
                      </Typography>
                    </Grid>
                    {reservation.notes && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">{reservation.notes}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};
